import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TackLogo } from "@/components/TackLogo";
import { openCookiePreferences } from "@/components/CookieBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

type SiteFooterProps = {
  variant?: "marketing" | "legal";
};

function LegalLinks() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <Link to="/" className="hover:text-primary transition-colors">{t("footer.ourStory") /* fallback */ ? t("common.home") : "Home"}</Link>
      <span aria-hidden>·</span>
      <Link to="/terms" className="hover:text-primary transition-colors">{t("footer.terms")}</Link>
      <span aria-hidden>·</span>
      <Link to="/privacy" className="hover:text-primary transition-colors">{t("footer.privacy")}</Link>
      <span aria-hidden>·</span>
      <Link to="/refund" className="hover:text-primary transition-colors">{t("footer.refunds")}</Link>
      <span aria-hidden>·</span>
      <Link to="/dpa" className="hover:text-primary transition-colors">{t("footer.dpa")}</Link>
      <span aria-hidden>·</span>
      <Link to="/status" className="hover:text-primary transition-colors">{t("footer.status")}</Link>
      <span aria-hidden>·</span>
      <button
        type="button"
        onClick={openCookiePreferences}
        className="hover:text-primary transition-colors underline-offset-2 hover:underline"
      >
        {t("footer.cookiePrefs")}
      </button>
    </div>
  );
}

export function SiteFooter({ variant = "legal" }: SiteFooterProps) {
  const { t } = useTranslation();

  if (variant === "marketing") {
    return (
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-5">
          <div className="w-full flex flex-col items-center gap-3">
            <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
              <TackLogo size="sm" />
            </Link>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <Link to="/why-tack" className="hover:text-primary transition-colors">{t("footer.ourStory")}</Link>
              <Link to="/pricing" className="hover:text-primary transition-colors">{t("footer.pricing")}</Link>
              <Link to="/faq" className="hover:text-primary transition-colors">{t("footer.faq")}</Link>
              <LanguageSwitcher variant="dark" />
            </div>
          </div>
          <div className="w-full flex flex-col items-center gap-2 pt-2 border-t border-border/60">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70">{t("footer.legal")}</span>
            </div>
            <LegalLinks />
            <p className="text-xs text-muted-foreground text-center">{t("footer.copyright")}</p>
            <p className="text-[11px] text-muted-foreground/70 text-center">{t("landing.availabilityBadge")}</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border py-6 px-4 mt-8">
      <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
        <LegalLinks />
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="dark" />
        </div>
        <p className="text-xs text-muted-foreground text-center">{t("footer.copyright")}</p>
        <p className="text-[11px] text-muted-foreground/70 text-center">{t("landing.availabilityBadge")}</p>
      </div>
    </footer>
  );
}
